import { formatARS, PAYMENT_METHOD_LABELS, PAYMENT_METHODS } from '@gl/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Lock, ReceiptText, UtensilsCrossed, Wallet, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { CashTodaySummary } from '../caja/CashTodaySummary';
import { formatBusinessDate } from '../caja/format';
import { cajaKeys, stats, useCashToday } from '../caja/queries';
import { OverviewCards } from '../estadisticas/EstadisticasPage';
import { useCatalogQuery } from '../menu/useCatalog';
import { addDays, currentBusinessDate } from '../estadisticas/ranges';
import { FirstSteps } from './FirstSteps';

/** `?primeros-pasos=1`: viene del onboarding (local recién creado). */
export const WELCOME_PARAM = 'primeros-pasos';

function QuickLink({ to, icon: Icon, title, hint }: { to: string; icon: LucideIcon; title: string; hint: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl border bg-card p-4 transition hover:border-primary/50 active:scale-[0.99]"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">{hint}</span>
      </span>
      <ChevronRight className="size-4 text-muted-foreground" />
    </Link>
  );
}

export function ResumenPage() {
  const local = useCurrentLocal();
  const { user } = useAuth();
  const isManager = useHasRole('OWNER', 'MANAGER');
  const today = useCashToday(local.id);
  const catalog = useCatalogQuery(local.id);
  const [params] = useSearchParams();
  const bd = useMemo(
    () => currentBusinessDate(local.timezone, local.businessDayStartHour),
    [local.timezone, local.businessDayStartHour],
  );
  const week = { from: addDays(bd, -6), to: bd };
  const overview = useQuery({
    queryKey: [...cajaKeys.stats(local.id), 'overview', week, {}],
    queryFn: () => stats.overview(local.id, week),
    enabled: isManager,
  });
  const t = today.data;
  const methods = t ? PAYMENT_METHODS.filter((m) => t.byPaymentMethod[m]) : [];

  return (
    <div className="grid gap-6">
      <PageHeader
        title={`Hola${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        description={
          <span className="inline-flex items-center gap-2">
            {local.name} · {t ? formatBusinessDate(t.businessDate) : '…'}
            {t?.isClosed && (
              <Badge variant="secondary">
                <Lock className="size-3" /> Caja cerrada
              </Badge>
            )}
          </span>
        }
      />

      {isManager && !local.isDemo && (
        <FirstSteps
          local={local}
          catalog={catalog.data}
          todaySales={t?.salesCount ?? 0}
          welcome={params.has(WELCOME_PARAM)}
        />
      )}

      <section className="grid gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Hoy</h2>
        <CashTodaySummary data={t} />
        {methods.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {methods.map((m) => (
              <span key={m}>
                {PAYMENT_METHOD_LABELS[m]}: <span className="font-medium text-foreground tabular-nums">{formatARS(t!.byPaymentMethod[m] ?? 0)}</span>
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-2 sm:grid-cols-3">
        <QuickLink to="../caja" icon={Wallet} title="Caja" hint="Cargar una venta, gasto o ingreso" />
        <QuickLink to="../menu" icon={UtensilsCrossed} title="Menú" hint="Productos, precios y disponibilidad" />
        {isManager && (
          <QuickLink
            to="../caja/cierres"
            icon={ReceiptText}
            title="Cierre de caja"
            hint={t?.isClosed ? 'Ver el cierre de hoy' : 'Arqueo y cierre del día'}
          />
        )}
      </section>

      {isManager && (
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="text-base">Últimos 7 días</CardTitle>
            <Link to="../estadisticas" className="text-sm font-medium text-primary hover:underline">
              Ver estadísticas
            </Link>
          </CardHeader>
          <CardContent>
            <OverviewCards data={overview.data} />
            {overview.data && (
              <p className="mt-2 text-xs text-muted-foreground">Comparado con los 7 días anteriores.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
